import { useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import {
  ArrowLeft,
  MapPin,
  Calendar,
  Phone,
  Mail,
  User,
  Dog,
  Scale,
  Palette,
  Wrench,
  Pencil,
  Trash2,
  CheckCircle,
  Clock,
  CalendarPlus,
} from 'lucide-react';
import { GoAlertFill } from 'react-icons/go';
import { Button } from '../../components/ui/button';
import LikeButton from '../../components/LikeButton/LikeButton';
import ChatStartButton from '../../components/ChatComponents/ChatStartButton/ChatStartButton';
import DeleteConfirmationModal from '../../components/DeleteConfirmationModal/DeleteConfirmationModal';
import EditPostModal from '../../components/EditPostModal/EditPostModal';
import ExtendPostModal from '../../components/ExtendPostModal/ExtendPostModal';
import ExpiredPostView from '../../components/ExpiredPostView/ExpiredPostView';
import PostActionsMenu from '../../components/PostActionsMenu/PostActionsMenu';
import AnimatedLoadingScreen from '../../components/UIFeedback/AnimatedLoadingScreen/AnimatedLoadingScreen';
import ErrorScreen from '../../components/UIFeedback/ErrorScreen/ErrorScreen';
import useFetchNotificationById from '../../hooks/notifications/useFetchNotificationById';
import usePostActions from '../../hooks/posts/usePostActions';
import { FEED_TYPES } from '../../hooks/notifications/useCombinedFeed';
import { formatDate } from '../../hooks/utils';
import {
  canExtendPost,
  EXTEND_WINDOW_DAYS,
  formatExpirationDate,
  parseExpiresAt,
} from '../../utils/postLifecycle';

/**
 * Maps notification type to the corresponding itemType for LikeButton
 */
const getItemType = (type) => {
  switch (type) {
    case FEED_TYPES.LOST:
      return 'LostAnimal';
    case FEED_TYPES.FOUND:
      return 'FoundAnimal';
    case FEED_TYPES.PSA:
      return 'PSA';
    case FEED_TYPES.PET_SERVICE:
      return 'PetService';
    default:
      return null;
  }
};

/**
 * Individual notification detail page
 * Route: /post/:id
 */
const NotificationDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { t, i18n } = useTranslation();
  
  const currentUser = useSelector((state) => state.auth.user);

  const knownType = location.state?.postType;

  const { data, type, loading, error, refetch } = useFetchNotificationById(
    id,
    knownType,
  );

  // Post actions state
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showExtendModal, setShowExtendModal] = useState(false);
  const { deletePost, toggleReunited, isDeleting, isUpdating } = usePostActions();

  const handleBack = () => {
    if (window.history.length > 2) {
      navigate(-1);
    } else {
      navigate('/');
    }
  };

  const handleShare = async () => {
    const url = window.location.href;
    const title = data?.title || data?.animalName || t('notification', 'Notification');
    
    if (navigator.share) {
      try {
        await navigator.share({ title, url });
      } catch (err) {
        if (err.name !== 'AbortError') {
          await navigator.clipboard.writeText(url);
        }
      }
    } else {
      await navigator.clipboard.writeText(url);
    }
  };

  const handleEdit = () => {
    setShowEditModal(true);
  };

  const handleEditSuccess = () => {
    setShowEditModal(false);
  };

  const handleDeleteClick = () => {
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    const result = await deletePost(id, type);
    if (result.success) {
      setShowDeleteModal(false);
      navigate('/');
    }
  };

  const handleDeleteCancel = () => {
    setShowDeleteModal(false);
  };

  const handleMarkReunited = async () => {
    const result = await toggleReunited(id, type, data?.reunited);
    if (result.success) {
      await refetch();
    }
  };

  // Loading state
  if (loading) {
    return <AnimatedLoadingScreen message={t('loading_notification', 'Loading...')} />;
  }

  // Error state
  if (error || !data) {
    return (
      <ErrorScreen>
        <div className="text-center py-8">
          <h2 className="text-xl font-semibold mb-2">
            {t('notification_not_found', 'Notification Not Found')}
          </h2>
          <p className="text-muted-foreground mb-4">
            {t('notification_not_found_desc', 'This notification may have been removed or the link is invalid.')}
          </p>
        </div>
      </ErrorScreen>
    );
  }

  // Determine if current user owns this post
  const isOwnPost = currentUser && data.userId === currentUser._id;

  const expiresAtDate = parseExpiresAt(data?.expiresAt);
  const isExpired = expiresAtDate ? expiresAtDate.getTime() < Date.now() : false;
  const formattedExpiresAt = formatExpirationDate(expiresAtDate, i18n.language);

  if (isExpired && !isOwnPost) {
    return <ExpiredPostView />;
  }

  const extendEligibility = expiresAtDate
    ? canExtendPost({ expiresAt: expiresAtDate })
    : { allowed: true, reason: 'unknown-expires-at' };

  const expirationLabel = isExpired
    ? t('post_status_expired', 'Expired')
    : formattedExpiresAt
      ? t('post_status_expires_on', 'Expires {{date}}', {
          date: formattedExpiresAt,
        })
      : null;

  const extendDisabledTooltip = !extendEligibility.allowed
    ? t(
        'extend_post_disabled_tooltip',
        'You can extend within {{count}} days of expiration, or any time after it has expired.',
        { count: EXTEND_WINDOW_DAYS },
      )
    : undefined;

  const renderInlineExpirationRow = () => {
    if (!isOwnPost || !expirationLabel) return null;
    return (
      <div
        className={`flex items-center gap-2 text-[10px] sm:text-xs ${isExpired ? 'text-red-600 font-medium' : 'text-muted-foreground'}`}
      >
        <Clock className="w-3.5 h-3.5 sm:w-4 sm:h-4 flex-shrink-0" />
        <span>{expirationLabel}</span>
      </div>
    );
  };

  const renderStackedExpirationRow = () => {
    if (!isOwnPost || !expirationLabel) return null;
    const labelText = isExpired
      ? t('post_status_expired', 'Expired')
      : t('post_status_expires_label', 'Expires');
    return (
      <div className="flex items-start gap-2 sm:gap-3">
        <Clock
          className={`w-3.5 h-3.5 sm:w-4 sm:h-4 mt-0.5 flex-shrink-0 ${isExpired ? 'text-red-600' : 'text-primary'}`}
        />
        <div className="flex flex-col min-w-0">
          <span
            className={`text-[10px] sm:text-xs ${isExpired ? 'text-red-600' : 'text-muted-foreground'}`}
          >
            {labelText}
          </span>
          {!isExpired && formattedExpiresAt && (
            <span className="text-xs sm:text-sm font-medium text-foreground">
              {formattedExpiresAt}
            </span>
          )}
        </div>
      </div>
    );
  };

  const extendModalPost = {
    _id: data._id,
    postType: type,
    expiresAt: data.expiresAt,
    lastExtendedAt: data.lastExtendedAt,
    title: data.title,
    animalName: data.animalName,
  };

  const extendPostModalSlot = (
    <ExtendPostModal
      isOpen={showExtendModal}
      onClose={() => setShowExtendModal(false)}
      post={extendModalPost}
      onExtended={() => {
        refetch();
      }}
    />
  );

  if (type === FEED_TYPES.PET_SERVICE) {
    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="min-h-screen bg-background"
      >
        <div className="sticky top-0 z-30 flex items-center justify-between px-3 sm:px-4 py-2.5 sm:py-3 bg-white border-b border-border">
          <button
            onClick={handleBack}
            className="flex items-center justify-center w-9 h-9 sm:w-10 sm:h-10 rounded-full hover:bg-muted transition-colors"
            aria-label={t('go_back', 'Go Back')}
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <h1 className="text-sm sm:text-base font-semibold text-foreground">
            {t('pet_service', 'Pet Service')}
          </h1>
          <div className="w-9 h-9 sm:w-10 sm:h-10" />
        </div>

        <div className="p-2 sm:p-4 max-w-2xl mx-auto">
          <div className="bg-white rounded-xl border border-border overflow-hidden">
            {/* Service Image (if available) */}
            {data.imageUrl && (
              <div className="relative w-full">
                <img
                  src={data.imageUrl}
                  alt={data.title}
                  className="w-full aspect-[16/9] object-cover"
                />
                <div className="absolute top-3 left-3">
                  <span className="text-xs px-2.5 py-1 rounded-full font-semibold shadow-sm bg-purple-100 text-purple-700">
                    {t('pet_service', 'Pet Service')}
                  </span>
                </div>
              </div>
            )}
            
            {/* Header (only show if no image) */}
            {!data.imageUrl && (
              <div className="flex items-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-2.5 sm:py-3 bg-purple-50 border-b border-purple-100">
                <Wrench className="w-4 h-4 sm:w-5 sm:h-5 text-purple-600 flex-shrink-0" />
                <span className="text-xs sm:text-sm font-semibold text-purple-900">
                  {t('pet_service', 'Pet Service')}
                </span>
              </div>
            )}
            
            <div className="p-4 sm:p-6 space-y-3 sm:space-y-4">
              <div className="flex items-start justify-between gap-3">
                <h2 className="text-lg sm:text-xl font-bold text-foreground flex-1">{data.title}</h2>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <LikeButton postId={data._id} itemType={getItemType(type)} />
                  <PostActionsMenu
                    isOwner={isOwnPost}
                    postUserId={data.userId}
                    postId={data._id}
                    postType={type}
                    onEdit={handleEdit}
                    onDelete={handleDeleteClick}
                    onShare={handleShare}
                    showReunitedOption={false}
                  />
                </div>
              </div>
              <p className="text-sm sm:text-base text-muted-foreground leading-relaxed whitespace-pre-wrap">
                {data.description}
              </p>
              
              <div className="flex flex-col gap-2 pt-3 sm:pt-4 border-t border-border">
                <div className="flex items-center gap-2 text-[10px] sm:text-xs text-muted-foreground">
                  <Calendar className="w-3.5 h-3.5 sm:w-4 sm:h-4 flex-shrink-0" />
                  <span>{t('posted', 'Posted')}: {formatDate(data.createdAt)}</span>
                </div>
                {renderInlineExpirationRow()}
              </div>

              {/* Owner Actions for Pet Service */}
              {isOwnPost && (
                <div className="flex flex-wrap gap-3 pt-3 border-t border-border">
                  <Button
                    onClick={handleEdit}
                    variant="outline"
                    size="sm"
                    className="flex items-center gap-2"
                  >
                    <Pencil className="w-4 h-4" />
                    {t('edit', 'Edit')}
                  </Button>
                  <Button
                    onClick={() => setShowExtendModal(true)}
                    variant="outline"
                    size="sm"
                    disabled={!extendEligibility.allowed}
                    title={extendDisabledTooltip}
                    className="flex items-center gap-2"
                  >
                    <CalendarPlus className="w-4 h-4" />
                    {t('extend_post_button', 'Extend post')}
                  </Button>
                  <Button
                    onClick={handleDeleteClick}
                    variant="outline"
                    size="sm"
                    className="flex items-center gap-2 border-red-200 text-red-600 hover:bg-red-50"
                  >
                    <Trash2 className="w-4 h-4" />
                    {t('delete', 'Delete')}
                  </Button>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Delete Confirmation Modal */}
        <DeleteConfirmationModal
          isOpen={showDeleteModal}
          onConfirm={handleDeleteConfirm}
          onCancel={handleDeleteCancel}
          isDeleting={isDeleting}
          postTitle={data?.title || ''}
        />

        {/* Edit Post Modal */}
        <EditPostModal
          isOpen={showEditModal}
          onClose={() => setShowEditModal(false)}
          postData={data}
          postType={type}
          onSuccess={handleEditSuccess}
        />

        {extendPostModalSlot}
      </motion.div>
    );
  }

  // Render PSA Detail
  if (type === FEED_TYPES.PSA) {
    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="min-h-screen bg-background"
      >
        {/* Header */}
        <div className="sticky top-0 z-30 flex items-center justify-between px-3 sm:px-4 py-2.5 sm:py-3 bg-white border-b border-border">
          <button
            onClick={handleBack}
            className="flex items-center justify-center w-9 h-9 sm:w-10 sm:h-10 rounded-full hover:bg-muted transition-colors"
            aria-label={t('go_back', 'Go Back')}
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <h1 className="text-sm sm:text-base font-semibold text-foreground">
            {t('psa', 'Announcement')}
          </h1>
          <div className="w-9 h-9 sm:w-10 sm:h-10" />
        </div>

        {/* Content */}
        <div className="p-2 sm:p-4 max-w-2xl mx-auto">
          <div className="bg-white rounded-xl border border-border overflow-hidden">
            {/* PSA Header */}
            <div className="flex items-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-2.5 sm:py-3 bg-gradient-to-r from-amber-50 to-amber-50/50 border-b border-amber-100">
              <GoAlertFill className="w-4 h-4 sm:w-5 sm:h-5 text-amber-600 flex-shrink-0" />
              <span className="text-xs sm:text-sm font-semibold text-amber-800">
                {t('psa_short', 'PSA')}
              </span>
            </div>

            {/* PSA Image (if exists) */}
            {data.imageUrl && (
              <div className="relative w-full aspect-[4/3] bg-gray-100">
                <img
                  src={data.imageUrl}
                  alt={data.title}
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
              </div>
            )}
            
            {/* PSA Body */}
            <div className="p-4 sm:p-6 space-y-3 sm:space-y-4">
              {/* Title, Bookmark, and Actions Menu */}
              <div className="flex items-start justify-between gap-3">
                <h2 className="text-lg sm:text-xl font-bold text-foreground flex-1">{data.title}</h2>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <LikeButton postId={data._id} itemType={getItemType(type)} />
                  <PostActionsMenu
                    isOwner={isOwnPost}
                    postUserId={data.userId}
                    postId={data._id}
                    postType={type}
                    onEdit={handleEdit}
                    onDelete={handleDeleteClick}
                    onShare={handleShare}
                    showReunitedOption={false}
                  />
                </div>
              </div>
              <p className="text-sm sm:text-base text-muted-foreground leading-relaxed whitespace-pre-wrap">
                {data.description}
              </p>
              
              <div className="flex flex-col gap-2 pt-3 sm:pt-4 border-t border-border">
                <div className="flex items-center gap-2 text-[10px] sm:text-xs text-muted-foreground">
                  <Calendar className="w-3.5 h-3.5 sm:w-4 sm:h-4 flex-shrink-0" />
                  <span>{t('posted', 'Posted')}: {formatDate(data.createdAt)}</span>
                </div>
                {data.startDate && (
                  <div className="flex items-center gap-2 text-[10px] sm:text-xs text-muted-foreground">
                    <Calendar className="w-3.5 h-3.5 sm:w-4 sm:h-4 flex-shrink-0" />
                    <span>{t('starts', 'Starts')}: {formatDate(data.startDate)}</span>
                  </div>
                )}
                {data.endDate && (
                  <div className="flex items-center gap-2 text-[10px] sm:text-xs text-muted-foreground">
                    <Calendar className="w-3.5 h-3.5 sm:w-4 sm:h-4 flex-shrink-0" />
                    <span>{t('ends', 'Ends')}: {formatDate(data.endDate)}</span>
                  </div>
                )}
                {renderInlineExpirationRow()}
              </div>

              {/* Owner Actions for PSA */}
              {isOwnPost && (
                <div className="flex flex-wrap gap-3 pt-3 border-t border-border">
                  <Button
                    onClick={handleEdit}
                    variant="outline"
                    size="sm"
                    className="flex items-center gap-2"
                  >
                    <Pencil className="w-4 h-4" />
                    {t('edit', 'Edit')}
                  </Button>
                  <Button
                    onClick={() => setShowExtendModal(true)}
                    variant="outline"
                    size="sm"
                    disabled={!extendEligibility.allowed}
                    title={extendDisabledTooltip}
                    className="flex items-center gap-2"
                  >
                    <CalendarPlus className="w-4 h-4" />
                    {t('extend_post_button', 'Extend post')}
                  </Button>
                  <Button
                    onClick={handleDeleteClick}
                    variant="outline"
                    size="sm"
                    className="flex items-center gap-2 border-red-200 text-red-600 hover:bg-red-50"
                  >
                    <Trash2 className="w-4 h-4" />
                    {t('delete', 'Delete')}
                  </Button>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Delete Confirmation Modal */}
        <DeleteConfirmationModal
          isOpen={showDeleteModal}
          onConfirm={handleDeleteConfirm}
          onCancel={handleDeleteCancel}
          isDeleting={isDeleting}
          postTitle={data?.title || ''}
        />

        {/* Edit Post Modal */}
        <EditPostModal
          isOpen={showEditModal}
          onClose={() => setShowEditModal(false)}
          postData={data}
          postType={type}
          onSuccess={handleEditSuccess}
        />

        {extendPostModalSlot}
      </motion.div>
    );
  }

  // Render Lost/Found Animal Detail
  const isLost = type === FEED_TYPES.LOST;
  const dateField = isLost ? data.dateLost : data.dateFound;
  const locationField = isLost ? data.lastSeen : data.foundNear;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="min-h-screen bg-background"
    >
      {/* Header */}
      <div className="sticky top-0 z-30 flex items-center justify-between px-3 sm:px-4 py-2.5 sm:py-3 bg-white border-b border-border">
        <button
          onClick={handleBack}
          className="flex items-center justify-center w-9 h-9 sm:w-10 sm:h-10 rounded-full hover:bg-muted transition-colors"
          aria-label={t('go_back', 'Go Back')}
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
        <h1 className="text-sm sm:text-base font-semibold text-foreground">
          {isLost ? t('lost', 'Lost') : t('found', 'Found')}
        </h1>
        <div className="w-9 h-9 sm:w-10 sm:h-10" />
      </div>

      {/* Image */}
      {data.animalImageUrl && (
        <div className="relative w-full bg-gray-100 sm:max-w-2xl sm:mx-auto sm:mt-4 sm:rounded-xl overflow-hidden">
          <img
            src={data.animalImageUrl}
            alt={data.animalName || t('animal', 'Animal')}
            className="w-full aspect-[4/3] object-cover"
          />
          {/* Status Badge */}
          <span className={`absolute top-3 right-3 sm:top-4 sm:right-4 px-2 sm:px-3 py-0.5 sm:py-1 rounded-full text-[10px] sm:text-xs font-semibold ${
            data.reunited
              ? 'bg-green-500 text-white'
              : isLost 
                ? 'bg-red-100 text-red-700' 
                : 'bg-green-100 text-green-700'
          }`}>
            {data.reunited 
              ? t('reunited', 'Reunited!')
              : isLost 
                ? t('lost', 'Lost') 
                : t('found', 'Found')
            }
          </span>
        </div>
      )}

      {/* Content */}
      <div className="p-2 sm:p-4 max-w-2xl mx-auto space-y-3 sm:space-y-4">
        {/* Animal Info Card */}
        <div className="bg-white rounded-xl border border-border p-4 sm:p-6 space-y-3 sm:space-y-4">
          {/* Title, Bookmark, and Actions Menu */}
          <div className="flex items-start justify-between gap-3">
            <h2 className="text-xl sm:text-2xl font-bold text-foreground flex-1">
              {data.animalName || (isLost ? t('lost_animal', 'Lost Animal') : t('found_animal', 'Found Animal'))}
            </h2>
            <div className="flex items-center gap-2 flex-shrink-0">
              <LikeButton postId={data._id} itemType={getItemType(type)} />
              <PostActionsMenu
                isOwner={isOwnPost}
                postUserId={data.userId}
                postId={data._id}
                postType={type}
                onEdit={handleEdit}
                onDelete={handleDeleteClick}
                onMarkReunited={handleMarkReunited}
                onShare={handleShare}
                isReunited={data.reunited}
                showReunitedOption={true}
              />
            </div>
          </div>
          
          {/* Reward Badge */}
          {isLost && data.rewardAmount && data.rewardAmount > 0 && (
            <div className="inline-flex items-center px-2 sm:px-3 py-1 sm:py-1.5 bg-yellow-50 text-yellow-800 rounded-lg text-xs sm:text-sm font-semibold">
              💰 {t('reward', 'Reward')}: ${data.rewardAmount}
            </div>
          )}

          {/* Details Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
            {data.animalType && (
              <div className="flex items-start gap-2 sm:gap-3">
                <Dog className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-muted-foreground mt-0.5 flex-shrink-0" />
                <div className="flex flex-col min-w-0">
                  <span className="text-[10px] sm:text-xs text-muted-foreground uppercase tracking-wide">{t('type', 'Type')}</span>
                  <span className="text-xs sm:text-sm font-medium text-foreground">{data.animalType}</span>
                </div>
              </div>
            )}
            
            {data.breed && (
              <div className="flex items-start gap-2 sm:gap-3">
                <Dog className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-muted-foreground mt-0.5 flex-shrink-0" />
                <div className="flex flex-col min-w-0">
                  <span className="text-[10px] sm:text-xs text-muted-foreground uppercase tracking-wide">{t('breed', 'Breed')}</span>
                  <span className="text-xs sm:text-sm font-medium text-foreground">{data.breed}</span>
                </div>
              </div>
            )}

            {data.sex && (
              <div className="flex items-start gap-2 sm:gap-3">
                <User className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-muted-foreground mt-0.5 flex-shrink-0" />
                <div className="flex flex-col min-w-0">
                  <span className="text-[10px] sm:text-xs text-muted-foreground uppercase tracking-wide">{t('sex', 'Sex')}</span>
                  <span className="text-xs sm:text-sm font-medium text-foreground">{data.sex}</span>
                </div>
              </div>
            )}

            {data.estimatedWeight && (
              <div className="flex items-start gap-2 sm:gap-3">
                <Scale className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-muted-foreground mt-0.5 flex-shrink-0" />
                <div className="flex flex-col min-w-0">
                  <span className="text-[10px] sm:text-xs text-muted-foreground uppercase tracking-wide">{t('weight', 'Weight')}</span>
                  <span className="text-xs sm:text-sm font-medium text-foreground">{data.estimatedWeight}</span>
                </div>
              </div>
            )}

            {data.colorMarkings && data.colorMarkings.length > 0 && (
              <div className="flex items-start gap-2 sm:gap-3 sm:col-span-2">
                <Palette className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-muted-foreground mt-0.5 flex-shrink-0" />
                <div className="flex flex-col min-w-0">
                  <span className="text-[10px] sm:text-xs text-muted-foreground uppercase tracking-wide">{t('colors', 'Colors')}</span>
                  <span className="text-xs sm:text-sm font-medium text-foreground">
                    {Array.isArray(data.colorMarkings) ? data.colorMarkings.join(', ') : data.colorMarkings}
                  </span>
                </div>
              </div>
            )}

            {isLost && data.approachable !== undefined && (
              <div className="flex items-start gap-2 sm:gap-3">
                <User className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-muted-foreground mt-0.5 flex-shrink-0" />
                <div className="flex flex-col min-w-0">
                  <span className="text-[10px] sm:text-xs text-muted-foreground uppercase tracking-wide">{t('approachable', 'Approachable')}</span>
                  <span className="text-xs sm:text-sm font-medium text-foreground">
                    {data.approachable ? t('yes', 'Yes') : t('no', 'No')}
                  </span>
                </div>
              </div>
            )}
          </div>

          {/* Location & Date */}
          <div className="flex flex-col gap-2 sm:gap-3 pt-3 sm:pt-4 border-t border-border">
            {locationField && (
              <div className="flex items-start gap-2 sm:gap-3">
                <MapPin className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-primary mt-0.5 flex-shrink-0" />
                <div className="flex flex-col min-w-0">
                  <span className="text-[10px] sm:text-xs text-muted-foreground">
                    {isLost ? t('last_seen', 'Last Seen') : t('found_near', 'Found Near')}
                  </span>
                  <span className="text-xs sm:text-sm font-medium text-foreground break-words">{locationField}</span>
                </div>
              </div>
            )}
            
            {dateField && (
              <div className="flex items-start gap-2 sm:gap-3">
                <Calendar className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-primary mt-0.5 flex-shrink-0" />
                <div className="flex flex-col min-w-0">
                  <span className="text-[10px] sm:text-xs text-muted-foreground">
                    {isLost ? t('date_lost', 'Date Lost') : t('date_found', 'Date Found')}
                  </span>
                  <span className="text-xs sm:text-sm font-medium text-foreground">{formatDate(dateField)}</span>
                </div>
              </div>
            )}
            {renderStackedExpirationRow()}
          </div>
        </div>

        {/* Contact Section */}
        {!isOwnPost && data.userId && (
          <div className="bg-white rounded-xl border border-border p-4 sm:p-6 space-y-3 sm:space-y-4">
            <h3 className="text-sm sm:text-base font-semibold text-foreground">
              {t('contact_information', 'Contact Information')}
            </h3>
            
            {data.contactName && (
              <div className="flex items-center gap-2 sm:gap-3 py-1.5 sm:py-2">
                <User className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-muted-foreground flex-shrink-0" />
                <span className="text-sm text-foreground">{data.contactName}</span>
              </div>
            )}
            
            {data.contactPhone && (
              <a 
                href={`tel:${data.contactPhone}`} 
                className="flex items-center gap-2 sm:gap-3 py-1.5 sm:py-2 text-foreground hover:text-primary transition-colors"
              >
                <Phone className="w-3.5 h-3.5 sm:w-4 sm:h-4 flex-shrink-0" />
                <span className="text-sm">{data.contactPhone}</span>
              </a>
            )}
            
            {data.contactEmail && (
              <a 
                href={`mailto:${data.contactEmail}`} 
                className="flex items-center gap-2 sm:gap-3 py-1.5 sm:py-2 text-foreground hover:text-primary transition-colors"
              >
                <Mail className="w-3.5 h-3.5 sm:w-4 sm:h-4 flex-shrink-0" />
                <span className="text-sm truncate">{data.contactEmail}</span>
              </a>
            )}

            {/* Contact Button - Same for auth and non-auth */}
            <div className="pt-2 flex justify-center">
              <ChatStartButton 
                userId={data.userId} 
                postData={{
                  postId: data._id,
                  postType: isLost ? 'lost' : 'found',
                  postName: data.animalName || '',
                  postBreed: data.breed || '',
                  postImageUrl: data.animalImageUrl || '',
                  postLocation: isLost ? (data.lastSeen || '') : (data.foundNear || ''),
                  postDate: isLost ? (data.dateLost || data.createdAt || '') : (data.dateFound || data.createdAt || ''),
                }}
              />
            </div>
          </div>
        )}

        {/* Owner Actions Section */}
        {isOwnPost && (
          <div className="bg-white rounded-xl border border-border p-4 sm:p-6 space-y-3 sm:space-y-4">
            <h3 className="text-sm sm:text-base font-semibold text-foreground">
              {t('manage_your_post', 'Manage Your Post')}
            </h3>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {/* Mark Reunited Button */}
              <Button
                onClick={handleMarkReunited}
                disabled={isUpdating}
                variant="outline"
                className={`flex items-center justify-center gap-2 ${
                  data.reunited 
                    ? 'border-amber-200 text-amber-700 hover:bg-amber-50' 
                    : 'border-green-200 text-green-700 hover:bg-green-50'
                }`}
              >
                <CheckCircle className="w-4 h-4" />
                {data.reunited 
                  ? t('undo_reunited', 'Undo Reunited')
                  : t('mark_reunited', 'Mark as Reunited')
                }
              </Button>

              {/* Edit Button */}
              <Button
                onClick={handleEdit}
                variant="outline"
                className="flex items-center justify-center gap-2"
              >
                <Pencil className="w-4 h-4" />
                {t('edit_post', 'Edit Post')}
              </Button>

              {/* Extend Button */}
              <Button
                onClick={() => setShowExtendModal(true)}
                variant="outline"
                disabled={!extendEligibility.allowed}
                title={extendDisabledTooltip}
                className="flex items-center justify-center gap-2"
              >
                <CalendarPlus className="w-4 h-4" />
                {t('extend_post_button', 'Extend post')}
              </Button>

              {/* Delete Button */}
              <Button
                onClick={handleDeleteClick}
                variant="outline"
                className="flex items-center justify-center gap-2 border-red-200 text-red-600 hover:bg-red-50"
              >
                <Trash2 className="w-4 h-4" />
                {t('delete_post', 'Delete Post')}
              </Button>
            </div>
          </div>
        )}

      </div>

      {/* Delete Confirmation Modal */}
      <DeleteConfirmationModal
        isOpen={showDeleteModal}
        onConfirm={handleDeleteConfirm}
        onCancel={handleDeleteCancel}
        isDeleting={isDeleting}
        postTitle={data?.animalName || data?.title || ''}
      />

      {/* Edit Post Modal */}
      <EditPostModal
        isOpen={showEditModal}
        onClose={() => setShowEditModal(false)}
        postData={data}
        postType={type}
        onSuccess={handleEditSuccess}
      />

      {extendPostModalSlot}
    </motion.div>
  );
};

export default NotificationDetail;
